import { Field, Form, Formik } from 'formik';
import { useRouter } from '@uirouter/react';

import {
  AuthTypeOption,
  Provider,
} from '@/react/portainer/account/git-credentials/types';
import { GitCredentialSection } from '@/react/portainer/account/git-credentials/components/GitCredentialSection';

import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { LoadingButton } from '@@/buttons/LoadingButton';
import { Button } from '@@/buttons';

import {
  CredentialType,
  credentialTitles,
  GitCredentialFormValues,
} from '../../types';

import { validationSchema } from './GitCredentialsForm.validation';

type Props = {
  selectedProvider: CredentialType;
  isEditing?: boolean;
  isLoading: boolean;
  onSubmit: (formValues: GitCredentialFormValues) => void;
  credentialNames: string[];
  initialValues?: GitCredentialFormValues;
};

const defaultInitialValues: GitCredentialFormValues = {
  name: '',
  credentials: {
    username: '',
    personalAccessToken: '',
    provider: Provider.Custom,
    authType: AuthTypeOption.Basic,
  },
};

export function GitCredentialsForm({
  selectedProvider,
  isEditing = false,
  isLoading,
  onSubmit,
  credentialNames,
  initialValues = defaultInitialValues,
}: Props) {
  const router = useRouter();
  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={() => validationSchema(credentialNames, isEditing)}
      onSubmit={(values) => onSubmit(values)}
      validateOnMount
    >
      {({ values, errors, handleSubmit, isValid, dirty, setFieldValue }) => (
        <Form className="form-horizontal" onSubmit={handleSubmit} noValidate>
          <FormControl
            inputId="name"
            label="Credentials name"
            size="medium"
            errors={errors.name}
            required
          >
            <Field
              as={Input}
              name="name"
              id="name"
              maxLength={200}
              value={values.name}
              placeholder={`e.g. ${credentialTitles[selectedProvider]}-staging`}
              data-cy="cloudSettings-apiKeyName"
            />
          </FormControl>

          <GitCredentialSection
            provider={values.credentials.provider}
            authorizationType={values.credentials.authType}
            username={values.credentials.username ?? ''}
            password={values.credentials.personalAccessToken}
            isEditing={isEditing}
            errors={{
              username: errors.credentials?.username,
              password: errors.credentials?.personalAccessToken,
            }}
            onChange={({ provider, username, password, authorizationType }) => {
              if (provider !== undefined) {
                setFieldValue('credentials.provider', provider);
              }
              if (username !== undefined) {
                setFieldValue('credentials.username', username);
              }
              if (password !== undefined) {
                setFieldValue('credentials.personalAccessToken', password);
              }
              if (authorizationType !== undefined) {
                setFieldValue('credentials.authType', authorizationType);
              }
            }}
          />

          <div className="form-group">
            <div className="col-sm-12 mt-3">
              <LoadingButton
                disabled={!isValid || !dirty}
                data-cy="createCredentials-saveButton"
                isLoading={isLoading}
                className="!ml-0"
                loadingText="Saving credentials..."
              >
                {isEditing ? 'Update credentials' : 'Add credentials'}
              </LoadingButton>
              {isEditing && (
                <Button
                  color="default"
                  data-cy="createGitCredentials-cancelButton"
                  onClick={() =>
                    router.stateService.go(
                      'portainer.settings.sharedcredentials'
                    )
                  }
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}
